/**
 * Client-side domain-event bus -- the ONE seam for sidebar refresh events.
 *
 * Sidebar counters across the three app shells (SuperAdmin, Distributor,
 * Portal) hydrate from one per-layout dashboard-summary endpoint. A mutation
 * on a child page must be able to tell the sidebar "something changed, refetch"
 * without either side importing the other. This bus is the generic transport;
 * the sidebar-refresh CONVENTION on top of it lives in ``sidebarRefresh.js``:
 *
 *   - A mutation store action calls ``emitSidebarRefresh('<summary-key>')`` on
 *     success -- ONE generic ``sidebar:refresh`` event carrying the tab's
 *     summary-store key + effective tenant (no per-domain event vocabulary).
 *   - Each layout's summary store subscribes to that single event and
 *     coarse-refetches when the event's ``id`` is a key it owns (delegation;
 *     see distributorSummary.subscribe).
 *
 * Transport: a plain ``Map<type, Set<fn>>`` inside a Pinia store -- no new
 * dependency (design Section 3 chose Pinia-native over mitt; the app is
 * already all-Pinia). The store exposes only an ``emit``/``on`` facade so
 * consumers never touch Pinia internals; if the transport ever changes
 * (mitt, backend push), only this file's internals move.
 *
 * Design: docs/design/2026-07-14-sidebar-event-driven-refresh-design.md
 * (Section 3 transport, Section 4 taxonomy).
 */
import { defineStore } from 'pinia'

export const useEventBus = defineStore('eventBus', () => {
    // type -> Set of handler fns. A Set gives idempotent register + O(1)
    // remove, so the unsubscribe fn returned by on() is cheap.
    const handlers = new Map()

    /**
     * Register ``fn`` for ``type``. Returns an unsubscribe fn that removes
     * exactly this handler (safe to call more than once).
     */
    function on(type, fn) {
        if (!handlers.has(type)) handlers.set(type, new Set())
        handlers.get(type).add(fn)
        return () => {
            handlers.get(type)?.delete(fn)
        }
    }

    /**
     * Deliver ``payload`` to every handler registered for ``type``. No
     * handlers for the type is a silent no-op (an emitter may fire an event
     * no layer is listening for yet -- harmless).
     */
    function emit(type, payload) {
        handlers.get(type)?.forEach((fn) => fn(payload))
    }

    return { on, emit }
})
